import { Grid, Stack } from '@mui/material';
import { FormProvider, useFormContext } from 'react-hook-form';
import { z } from 'zod';

import ReportSeverity from '../../components/customer/dashboard/reportSeverity';
import ReportStatistics from '../../components/customer/dashboard/reportStatistics';
import ReportTopFilter from '../../components/customer/dashboard/reportTopFilters';
import {
  DEFAULT_VALUE,
  INITIAL_VALUE,
  usePartnerContext,
} from '../../contexts/partner/partnerContext';

import ReportByCaseManager from '~/components/customer/dashboard/reportByCaseManager';
import ReportsByDepartment from '~/components/customer/dashboard/reportsByDepartment';
import ReportsRFI from '~/components/customer/dashboard/reportsRFI';
import TotalReport from '~/components/customer/dashboard/totalReport';
import { isExperimental } from '~/hooks/global/useExperimental';
import { CheckMetricWrapper } from '~/pages/customer/dashboard/checkMetricWrapper';
import { useForm } from '~/utils/forms';
import { isoTimezoneRangeForLocalDate } from '~/utils/helpers';

// Helper function to add query parameters
const addQueryParam = (
  url: string,
  param: string,
  value: string | number | boolean | null,
) => {
  if (value !== null && value !== undefined) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${param}=${encodeURIComponent(value)}`;
  }
  return url;
};

export const getUrlString = (
  link: string,
  filterBy: string,
  date: string,
  dateEnd: string,
  tenantId: number,
  channelId: number | null,
  hideDaysWithoutReports?: boolean,
) => {
  let newLink = link;
  if (channelId) {
    newLink = addQueryParam(newLink, 'intake_channel', channelId);
  }
  if (filterBy && date) {
    const { startOfDayISO } = isoTimezoneRangeForLocalDate(new Date(date));
    if (filterBy === 'day') {
      newLink = addQueryParam(newLink, 'filter_by', 'week');
      newLink = addQueryParam(newLink, 'date', startOfDayISO);

      if (dateEnd) {
        const { endOfDayISO } = isoTimezoneRangeForLocalDate(new Date(dateEnd));
        newLink = addQueryParam(newLink, 'date_end', endOfDayISO);
      }
    } else if (filterBy === 'month' && dateEnd) {
      const { endOfDayISO } = isoTimezoneRangeForLocalDate(new Date(dateEnd));
      newLink = addQueryParam(newLink, 'filter_by', filterBy);
      newLink = addQueryParam(newLink, 'date', startOfDayISO);
      newLink = addQueryParam(newLink, 'date_end', endOfDayISO);
    }
  }

  if (tenantId !== INITIAL_VALUE && tenantId !== DEFAULT_VALUE) {
    newLink = addQueryParam(newLink, 'tenant_id', tenantId);
  }

  if (hideDaysWithoutReports !== undefined) {
    newLink = addQueryParam(
      newLink,
      'hide_days_without_reports',
      hideDaysWithoutReports,
    );
  }

  return newLink;
};

const dashboardFilterSchema = z.object({
  date_range: z.object({
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
  }),
});

export type DashboardFilterFields = z.infer<typeof dashboardFilterSchema>;
export const useDashoardFilter = () => useFormContext<DashboardFilterFields>();

const CustomerDashboard = () => {
  const { selectedTenant } = usePartnerContext();

  const filterForm = useForm({
    schema: dashboardFilterSchema,
    defaultValues: {
      date_range: {},
    },
  });

  return (
    <FormProvider {...filterForm}>
      <Stack gap={3}>
        <ReportStatistics tenantId={selectedTenant?.id} />
        <ReportTopFilter />
        <Grid container item xs={12} spacing={2} direction='row'>
          <Grid item xs={4}>
            <ReportSeverity />
          </Grid>
          <Grid item xs={8}>
            <ReportByCaseManager />
          </Grid>
        </Grid>
        <ReportsByDepartment />
        {isExperimental && <ReportsRFI />}
        <CheckMetricWrapper
          metricCategory='Total Report'
          metricName='Reports by Report type'
        >
          <TotalReport />
        </CheckMetricWrapper>
      </Stack>
    </FormProvider>
  );
};

export default CustomerDashboard;
